import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import styled from 'styled-components';

import MenuIcon from 'components/Header/MenuIcon';
import { COMMON_COLOR } from 'containers/Leaderboards/components/PhonePreviewWrapper';
import { useTenant } from 'hooks';
import { COMMON_COLOR as UtilsStylesColors } from 'utils/style';

import { sendAiChatMessage } from './actions';
import { AI_CHATBOT_SET_HEADER_CHIP_VISIBILITY } from './reducer';

type DashboardSuggestionsProps = {
  questions: string[];
};

const DashboardSuggestions: React.FC<DashboardSuggestionsProps> = ({ questions }) => {
  const { name: tenantName } = useTenant();

  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const loading = useSelector((state: any) => state.aiChatBot.loading);
  const messages = useSelector((state: any) => state.aiChatBot.messages);
  const tokens = useSelector((state: any) => state.aiChatBot.tokens);
  const open = useSelector((state: any) => state.aiChatBot.open);
  const isVisible = useSelector((state: any) => {
    // Keep visible when side drawer (eMind) is open, or when header chip is not visible
    return open || !state.aiChatBot.isHeaderChipVisible;
  });

  const onQuestionClick = (question: string) => {
    if (loading || tokens.unused_tokens === 0) return;
    dispatch({ type: AI_CHATBOT_SET_HEADER_CHIP_VISIBILITY, payload: true });
    const userMsg = { text: question, sender: 'user' };
    let newMessages = [...messages, userMsg, { text: '__LOADING_DOTS__', sender: 'bot' }];
    if (!open) {
      dispatch({ type: 'AI_CHATBOT_SET_OPEN', payload: true });
      setTimeout(() => {
        dispatch({ type: 'AI_CHATBOT_SEND_MESSAGE', payload: userMsg });
        dispatch({
          type: 'AI_CHATBOT_SEND_MESSAGE',
          payload: { text: '__LOADING_DOTS__', sender: 'bot' }
        });
        dispatch(sendAiChatMessage(question, newMessages, tenantName));
      }, 0);
    } else {
      dispatch({ type: 'AI_CHATBOT_SEND_MESSAGE', payload: userMsg });
      dispatch({
        type: 'AI_CHATBOT_SEND_MESSAGE',
        payload: { text: '__LOADING_DOTS__', sender: 'bot' }
      });
      dispatch(sendAiChatMessage(question, newMessages, tenantName));
    }
  };

  const chipsRef = React.useRef<HTMLDivElement>(null);

  const scrollChips = (direction: 'left' | 'right') => {
    const container = chipsRef.current;
    if (!container) return;
    const scrollAmount = 280;
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const [inputValue, setInputValue] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (tokens.unused_tokens !== 0 && e.key === 'Enter' && !e.shiftKey) {
      if (loading) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (tokens.unused_tokens === 0) return;
    const trimmed = inputValue.trim();
    if (trimmed.length === 0) return;
    onQuestionClick(trimmed);
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!isVisible) return;
    let timer: NodeJS.Timeout | null = null;
    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        dispatch({ type: AI_CHATBOT_SET_HEADER_CHIP_VISIBILITY, payload: true });
      }, 10000);
    };
    const ref = wrapperRef.current;
    if (ref) {
      const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
      events.forEach(event => {
        ref.addEventListener(event, resetTimer);
      });
      resetTimer();
      return () => {
        if (timer) clearTimeout(timer);
        events.forEach(event => {
          ref.removeEventListener(event, resetTimer);
        });
      };
    }
    return undefined;
  }, [isVisible, dispatch]);

  return (
    <AnimatedSuggestionWrapper
      isVisible={isVisible}
      onMouseLeave={() => {
        if (inputValue.trim().length === 0) {
          dispatch({ type: AI_CHATBOT_SET_HEADER_CHIP_VISIBILITY, payload: true });
        }
      }}
      ref={wrapperRef}
      id="ai-suggestions-header"
    >
      <SectionHeader>
        <ArrowButton onClick={() => scrollChips('left')} aria-label="Scroll left">
          <svg
            width="8"
            height="15"
            viewBox="0 0 8 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.83675 0.164663C7.94128 0.270235 8 0.413342 8 0.56255C8 0.711757 7.94128 0.854864 7.83675 0.960435L1.34665 7.50677L7.83675 14.0531C7.93535 14.1598 7.98903 14.301 7.98648 14.4469C7.98393 14.5928 7.92534 14.7319 7.82307 14.8351C7.7208 14.9382 7.58282 14.9973 7.43821 14.9999C7.29359 15.0025 7.15363 14.9483 7.04782 14.8489L0.16325 7.90466C0.0587159 7.79909 0 7.65598 0 7.50677C0 7.35757 0.0587159 7.21446 0.16325 7.10889L7.04782 0.164663C7.15248 0.0592232 7.29436 0 7.44228 0C7.59021 0 7.73209 0.0592232 7.83675 0.164663Z"
              fill="black"
              fillOpacity="0.3"
            />
          </svg>
        </ArrowButton>

        <ChipsContainer ref={chipsRef}>
          {questions.map((question, idx) => (
            <ChipButton
              key={idx}
              onClick={() => onQuestionClick(question)}
              disabled={loading || tokens.unused_tokens === 0}
              aria-disabled={loading || tokens.unused_tokens === 0}
            >
              <MenuIcon src={'sparkle_search'} /> {question}
            </ChipButton>
          ))}
        </ChipsContainer>
        <ArrowButton onClick={() => scrollChips('right')} aria-label="Scroll right">
          <svg
            width="8"
            height="18"
            viewBox="0 0 8 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.163249 16.9547C0.0587153 16.834 0 16.6705 0 16.4999C0 16.3294 0.0587153 16.1659 0.163249 16.0452L6.65335 8.56369L0.163249 1.08216C0.0646477 0.960173 0.0109687 0.798834 0.0135202 0.632129C0.0160718 0.465423 0.0746551 0.306368 0.176929 0.188471C0.279202 0.0705742 0.41718 0.00304157 0.561794 0.000100252C0.706408 -0.00284107 0.846367 0.0590385 0.952185 0.172703L7.83675 8.10896C7.94128 8.22961 8 8.39316 8 8.56369C8 8.73421 7.94128 8.89776 7.83675 9.01841L0.952185 16.9547C0.84752 17.0752 0.705643 17.1429 0.557717 17.1429C0.409791 17.1429 0.267914 17.0752 0.163249 16.9547Z"
              fill="black"
              fillOpacity="0.3"
            />
          </svg>
        </ArrowButton>
      </SectionHeader>

      {/* input section */}
      <InputSection>
        <div>
          <svg
            width="20"
            height="19"
            viewBox="0 0 20 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.02692 13.5117C7.22829 13.645 7.46891 13.7164 7.71543 13.7158C7.96195 13.7152 8.20217 13.6428 8.40284 13.5085C8.60626 13.3692 8.76053 13.1763 8.8451 12.9534L9.35593 11.4822C9.48701 11.1135 9.70774 10.7783 10.0007 10.5033C10.2936 10.2282 10.6507 10.0208 11.0438 9.89741L12.6335 9.41522C12.8685 9.33544 13.0709 9.18852 13.2121 8.99517C13.3534 8.80181 13.4264 8.57178 13.4208 8.33747C13.4153 8.10316 13.3315 7.87639 13.1812 7.68911C13.0309 7.50183 12.8217 7.36349 12.5832 7.29357L11.0118 6.81353C10.6183 6.69076 10.2607 6.48386 9.96715 6.20918C9.67362 5.9345 9.45223 5.59957 9.3205 5.23086L8.80396 3.74357C8.72078 3.52445 8.56684 3.33509 8.36355 3.20183C8.16025 3.06857 7.91772 2.99804 7.66968 3.00004C7.42164 3.00205 7.18044 3.07649 6.97962 3.21301C6.77881 3.34954 6.62837 3.54136 6.54923 3.76178L6.02697 5.26194C5.89587 5.62076 5.67966 5.94702 5.39461 6.21615C5.10957 6.48527 4.76312 6.69026 4.38135 6.81567L2.79287 7.29465C2.55928 7.3728 2.35744 7.51721 2.21536 7.70783C2.07329 7.89844 1.99803 8.1258 2.00004 8.35835C2.00205 8.5909 2.08123 8.81708 2.22658 9.00552C2.37193 9.19395 2.57625 9.33527 2.81116 9.40986L4.38135 9.8867C4.77684 10.0098 5.13601 10.2181 5.42996 10.4951C5.72392 10.772 5.94445 11.1097 6.07383 11.4811L6.59037 12.9717C6.67265 13.1903 6.82579 13.3788 7.02692 13.5117Z"
              fill="url(#paint0_linear_535_15944)"
            />
            <path
              d="M14.0402 17.8425C14.1571 17.9195 14.2915 17.97 14.4328 17.99C14.5741 18.0099 14.7185 17.9988 14.8544 17.9574C14.9903 17.916 15.1141 17.8456 15.2159 17.7516C15.3178 17.6576 15.3949 17.5427 15.4413 17.416L15.7247 16.5995C15.7861 16.4303 15.8877 16.2764 16.0218 16.1494C16.1567 16.0209 16.3212 15.9266 16.5018 15.8708L17.384 15.6008C17.5661 15.5419 17.7236 15.4306 17.8342 15.2827C17.9448 15.1349 18.0028 14.9581 17.9999 14.7775C17.997 14.597 17.9333 14.4219 17.818 14.2772C17.7027 14.1326 17.5416 14.0258 17.3577 13.9721L16.4847 13.7042C16.3041 13.6475 16.1401 13.5524 16.0053 13.4264C15.8704 13.3003 15.7686 13.1468 15.7076 12.9777L15.4196 12.1494C15.3577 11.979 15.2403 11.8312 15.0839 11.727C14.9274 11.6227 14.74 11.5672 14.5481 11.5684C14.3562 11.5696 14.1695 11.6274 14.0146 11.7336C13.8596 11.8398 13.7443 11.989 13.6848 12.1601L13.4025 12.9766C13.3442 13.1446 13.2453 13.2976 13.1134 13.4239C12.9816 13.5501 12.8205 13.646 12.6426 13.7042L11.7592 13.9742C11.5774 14.032 11.4197 14.1419 11.3083 14.2884C11.1968 14.435 11.1374 14.6106 11.1384 14.7906C11.1395 14.9706 11.2008 15.1456 11.3139 15.2911C11.4269 15.4365 11.5859 15.5448 11.7683 15.6008L12.6403 15.8655C12.8231 15.9233 12.9877 16.0187 13.1226 16.1451C13.2586 16.2727 13.3591 16.427 13.4174 16.5973L13.7065 17.4267C13.7691 17.5944 13.8857 17.7396 14.0402 17.8425Z"
              fill="url(#paint1_linear_535_15944)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_535_15944"
                x1="0.937332"
                y1="3.7192"
                x2="9.51105"
                y2="14.9916"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0.3" stopColor="#8B5CF6" />
                <stop offset="1" stopColor="#469BFF" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_535_15944"
                x1="10.5"
                y1="12"
                x2="15"
                y2="18"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0.3" stopColor="#8B5CF6" />
                <stop offset="1" stopColor="#469BFF" />
              </linearGradient>
            </defs>
          </svg>
          <input
            type="text"
            placeholder="Ask me about your data.."
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            aria-label="Type your message"
            autoFocus={true}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={inputValue.trim().length === 0 || tokens.unused_tokens === 0 || loading}
          aria-label="send"
        >
          <MenuIcon src={'send_arrow'} />
        </button>
      </InputSection>
    </AnimatedSuggestionWrapper>
  );
};

export default DashboardSuggestions;

const AnimatedSuggestionWrapper = styled.div<{ isVisible?: boolean }>`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 101;
  background: linear-gradient(90deg, #ede9fe 0%, #d6eff9 100%);
  padding: ${props => (props.isVisible ? '0.5rem 2.5rem' : '0 2.5rem')};
  max-height: ${props => (props.isVisible ? '500px' : '0')};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ArrowButton = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.625rem;
  svg {
    path {
      transition: fill 0.2s, fill-opacity 0.2s;
    }
  }

  &:hover {
    svg {
      path {
        fill: #595959;
        fill-opacity: 1;
      }
    }
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.375rem;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChipButton = styled.button`
  background: #fdfdfd;
  border-radius: 24px;
  padding: 0.25rem 0.375rem 0.25rem 0.25rem;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  font-style: Medium;
  leading-trim: NONE;
  line-height: 18px;
  letter-spacing: 0px;
  color: ${COMMON_COLOR.GREY};
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.2s, border 0.2s;
  &:hover {
    border: 1px solid #c3b6fd;
    background: #ffffff;
  }
`;

const InputSection = styled.div`
  margin-top: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: #ffffff;
  border: 1.5px solid #ddd6fe;
  border-radius: 8px;
  padding: 0.375rem 0.5rem;

  div {
    display: flex;
    align-items: center;
    flex: 1;

    input {
      flex: 1;
      margin-left: 0.5rem;
      border: none;
      outline: none;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 400;
      leading-trim: NONE;
      line-height: 18px;
      background: transparent;
    }
  }

  button {
    background: ${UtilsStylesColors.BLUE};
    border: none;
    cursor: pointer;
    height: 1.75rem;
    width: 1.75rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;

    &:hover {
      background: ${UtilsStylesColors.BLUE_2};
    }
  }

  button:disabled {
    background: ${UtilsStylesColors.GREY_7};
    cursor: not-allowed;
    opacity: 0.6;
    pointer-events: none;
  }
`;
